import React from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Shield, CheckCircle, Database, 
  Globe, Zap, FileCheck, AlertCircle, Calendar
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';

const ComprehensiveBreakdownsSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-8 relative z-10">
            {/* Left Side: Context */}
            <div className="w-[40%] flex flex-col justify-center space-y-6">
                
                {/* Agent Header (Inline) */}
                <div className="flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg w-fit mb-8">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                         <img src="/assets/amanda.png" alt="Amanda" className="w-full h-full object-cover" />
                    </div>
                    <div className="text-left">
                        <h3 className="text-lg font-bold text-slate-900 leading-none">Amanda</h3>
                        <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Insurance Verification</p>
                    </div>
                </div>

                <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                    Comprehensive Breakdowns <span className="text-blue-600">Synced Directly to Your PMS</span>.
                </h2>
                
                <p className="text-lg text-slate-600 leading-relaxed">
                    We leverage <strong>300+ direct dental payer integrations</strong> to retrieve granular, procedure-level details that other systems miss—including frequencies, age limitations, and downgrades.
                </p>

                <p className="text-lg text-slate-600 leading-relaxed">
                    Amanda automatically generates a full benefits PDF and uploads it directly to the patient's chart in Asha PMS, so your team has the full picture without lifting a finger.
                </p>

                <div className="space-y-4 pt-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600 mt-1">
                            <Globe className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg">300+ Payer Integrations</h4>
                            <p className="text-sm text-slate-500">Direct connections for real-time accuracy.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                        <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600 mt-1">
                            <Database className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg">Full PMS Write-Back</h4>
                            <p className="text-sm text-slate-500">PDFs uploaded to the correct patient folder automatically.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Graphic */}
            <div className="w-[60%] h-full flex items-center justify-center pl-8">
                <motion.div 
                    initial={{ y: 40, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="w-full max-w-2xl"
                >
                    {/* Benefits Breakdown Card */}
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden relative">
                        {/* Header */}
                        <div className="bg-slate-50 border-b border-slate-200 p-6 flex justify-between items-center">
                            <div>
                                <h3 className="font-bold text-xl text-slate-900 flex items-center gap-2">
                                    <Shield className="w-5 h-5 text-blue-600" />
                                    Benefits Breakdown
                                </h3>
                                <p className="text-sm text-slate-500">Patient: Sarah Johnson • Delta Dental PPO</p>
                            </div>
                            <Badge className="bg-green-100 text-green-700 border-green-200 gap-1 pl-2 pr-3 py-1">
                                <CheckCircle className="w-3.5 h-3.5" /> Verified Today
                            </Badge>
                        </div>

                        <div className="p-6 space-y-6">
                            
                            {/* General Info Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                                    <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Annual Max Remaining</div>
                                    <div className="text-2xl font-bold text-slate-900">$1,250.00</div>
                                    <div className="text-xs text-slate-400 mt-1">of $2,000.00 Total</div>
                                </div>
                                <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                                    <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Deductible Remaining</div>
                                    <div className="text-2xl font-bold text-slate-900">$0.00</div>
                                    <div className="text-xs text-slate-400 mt-1">Met for Year</div>
                                </div>
                            </div>

                            {/* Detailed Procedures List */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider px-2">
                                    <span>Procedure Details</span>
                                    <span>Coverage</span>
                                </div>
                                
                                {/* Item 1: Crown */}
                                <div className="border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors bg-white shadow-sm group">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-3">
                                            <Badge variant="outline" className="bg-slate-100 font-mono text-slate-700 border-slate-200">D2740</Badge>
                                            <div>
                                                <div className="font-bold text-slate-900">Crown - Porcelain/Ceramic</div>
                                                <div className="text-xs text-slate-500">Category: Major</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full text-sm">50% Covered</div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 text-xs bg-slate-50 p-3 rounded-lg border border-slate-100">
                                        <div>
                                            <span className="block text-slate-400 mb-0.5">History</span>
                                            <span className="font-semibold text-slate-700">Last: 10/12/2019</span>
                                        </div>
                                        <div>
                                            <span className="block text-slate-400 mb-0.5">Frequency</span>
                                            <span className="font-semibold text-slate-700">1 in 5 Years</span>
                                        </div>
                                        <div>
                                            <span className="block text-slate-400 mb-0.5">Age Limit</span>
                                            <span className="font-semibold text-slate-700">None</span>
                                        </div>
                                    </div>
                                    <div className="mt-2 flex items-center gap-2 text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded w-fit">
                                        <AlertCircle className="w-3 h-3" />
                                        <span>Downgrades to AMC if posterior</span>
                                    </div>
                                </div>

                                {/* Item 2: SRP */}
                                <div className="border border-slate-200 rounded-xl p-4 hover:border-blue-300 transition-colors bg-white shadow-sm">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-3">
                                            <Badge variant="outline" className="bg-slate-100 font-mono text-slate-700 border-slate-200">D4341</Badge>
                                            <div>
                                                <div className="font-bold text-slate-900">Periodontal Scaling</div>
                                                <div className="text-xs text-slate-500">Category: Basic</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full text-sm">80% Covered</div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 text-xs bg-slate-50 p-3 rounded-lg border border-slate-100">
                                        <div>
                                            <span className="block text-slate-400 mb-0.5">History</span>
                                            <span className="font-semibold text-slate-700">None on file</span>
                                        </div>
                                        <div>
                                            <span className="block text-slate-400 mb-0.5">Frequency</span>
                                            <span className="font-semibold text-slate-700">1 in 24 Months</span>
                                        </div>
                                        <div>
                                            <span className="block text-slate-400 mb-0.5">Quad Limit</span>
                                            <span className="font-semibold text-slate-700">2 Quads / Visit</span>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* Footer / PMS Sync */}
                        <div className="bg-slate-900 p-4 flex items-center justify-between text-white">
                            <div className="flex items-center gap-3">
                                <div className="p-1.5 bg-green-500/20 rounded">
                                    <FileCheck className="w-4 h-4 text-green-400" />
                                </div>
                                <div className="text-sm">
                                    <span className="font-bold block">Synced to PMS</span>
                                    <span className="text-xs text-slate-400">Benefits_Summary.pdf uploaded to Chart</span>
                                </div>
                            </div>
                            <Badge className="bg-blue-600 hover:bg-blue-500 text-white border-0 cursor-pointer">
                                View PDF
                            </Badge>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </div>
  );
};

export default ComprehensiveBreakdownsSlide;





















